import { MessageSquare, Send, Users, CheckCircle, FileText, ThumbsUp, UserX, Eye, Settings, Trash2, Plus, X, Save, BarChart3, ToggleLeft, ToggleRight, Bookmark, Copy, RefreshCw, Clock } from 'lucide-react';
import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useEventManageContext } from './EventManagePage';
import { getMessagesByEventId, sendDJMessage, Message, AudienceType, AUDIENCE_OPTIONS, getMessageReadStats, getEstimatedAttendeeCount } from '../../services/messages';
import { getCannedMessages, addCannedMessage, deleteCannedMessage, CannedMessage } from '../../services/cannedMessages';
import { getAllPollsForEvent, createPoll, updatePoll, closePoll, deletePoll, toggleResultVisibility, savePollTemplate, getPollTemplates, deletePollTemplate, resendPoll, buildSendNowUpdate, buildCancelAutoSendUpdate, Poll, PollQuestion, PollSettings, PollTemplate } from '../../services/polls';
import { describeAutoSend } from '../../services/pollLibrary';
import PollBuilder, { type PollBuilderValues } from '../../components/polls/PollBuilder';
import PollResults from '../../components/polls/PollResults';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';

export default function MessagesPage() {
  const { eventId } = useParams<{ eventId: string }>();
  const { event } = useEventManageContext();
  const isLocked = event?.locked ?? false;
  const { user } = useAuth();
  const [message, setMessage] = useState('');
  const [audience, setAudience] = useState<AudienceType>('all');
  const [messages, setMessages] = useState<Message[]>([]);
  const [showSentConfirmation, setShowSentConfirmation] = useState(false);
  const [estimatedAttendees, setEstimatedAttendees] = useState(0);
  const [messageReadStats, setMessageReadStats] = useState<Record<string, { readCount: number }>>({});

  // Canned messages state
  const [cannedTemplates, setCannedTemplates] = useState<CannedMessage[]>([]);
  const [showManageTemplates, setShowManageTemplates] = useState(false);
  const [newTemplateLabel, setNewTemplateLabel] = useState('');
  const [newTemplateText, setNewTemplateText] = useState('');

  // Poll state
  const [polls, setPolls] = useState<Poll[]>([]);
  const [showPollBuilder, setShowPollBuilder] = useState(false);
  const [viewingResultsFor, setViewingResultsFor] = useState<string | null>(null);
  // Poll template state
  const [pollTemplates, setPollTemplates] = useState<PollTemplate[]>([]);
  const [editingFromTemplate, setEditingFromTemplate] = useState<{ title: string; questions: PollQuestion[]; settings: PollSettings } | null>(null);
  const [showPollTemplates, setShowPollTemplates] = useState(false);

  // Load canned messages
  useEffect(() => {
    if (user?.uid) {
      const loadTemplates = async () => {
        const templates = await getCannedMessages(user.uid);
        setCannedTemplates(templates);
      };
      loadTemplates();
    }
  }, [user?.uid]);

  // Load messages on mount and periodically refresh for read counts
  useEffect(() => {
    if (eventId || event?.id) {
      const id = eventId || event?.id;
      const loadMessages = async () => {
        const eventMessages = await getMessagesByEventId(id!);
        setMessages(eventMessages);
        // Get estimated attendee count for percentage calculations
        setEstimatedAttendees(await getEstimatedAttendeeCount(id!));
        // Pre-fetch read stats for all messages
        const stats: Record<string, { readCount: number }> = {};
        for (const msg of eventMessages) {
          stats[msg.id] = await getMessageReadStats(msg.id);
        }
        setMessageReadStats(stats);
      };

      loadMessages();

      // Refresh messages every 5 seconds to get updated read counts
      const interval = setInterval(loadMessages, 5000);
      return () => clearInterval(interval);
    }
  }, [eventId, event?.id]);

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!message.trim()) {
      toast.error('Please enter a message');
      return;
    }

    const id = eventId || event?.id;
    if (!id) {
      toast.error('No event selected');
      return;
    }

    // Send the message
    const audienceOption = AUDIENCE_OPTIONS.find(a => a.id === audience);
    const newMessage = await sendDJMessage(id, message.trim(), audience, audienceOption?.label);

    // Update local state
    setMessages(prev => [...prev, newMessage]);

    // Clear input
    setMessage('');

    // Show confirmation
    setShowSentConfirmation(true);
    const audienceText = audienceOption?.label || 'attendees';
    toast.success(`Message sent to ${audienceText}!`);

    // Hide confirmation after 3 seconds
    setTimeout(() => {
      setShowSentConfirmation(false);
    }, 3000);
  };

  // Save current message text as a new template
  const handleSaveAsTemplate = async () => {
    if (!user?.uid || !message.trim()) return;
    const label = message.trim().substring(0, 40) + (message.trim().length > 40 ? '...' : '');
    const newMsg = await addCannedMessage(user.uid, label, message.trim());
    setCannedTemplates(prev => [...prev, newMsg]);
    toast.success('Saved as template');
  };

  // Save current message as template AND send it
  const handleSaveAndSend = async (e: React.FormEvent) => {
    if (user?.uid && message.trim()) {
      const label = message.trim().substring(0, 40) + (message.trim().length > 40 ? '...' : '');
      const newMsg = await addCannedMessage(user.uid, label, message.trim());
      setCannedTemplates(prev => [...prev, newMsg]);
    }
    await handleSend(e);
  };

  // Add custom template from management modal
  const handleAddCustomTemplate = async () => {
    if (!user?.uid || !newTemplateLabel.trim() || !newTemplateText.trim()) return;
    const newMsg = await addCannedMessage(user.uid, newTemplateLabel.trim(), newTemplateText.trim());
    setCannedTemplates(prev => [...prev, newMsg]);
    setNewTemplateLabel('');
    setNewTemplateText('');
    toast.success('Template added');
  };

  // Delete a template
  const handleDeleteTemplate = async (templateId: string) => {
    if (!user?.uid) return;
    await deleteCannedMessage(user.uid, templateId);
    setCannedTemplates(prev => prev.filter(t => t.id !== templateId));
    toast.success('Template deleted');
  };

  // Load polls
  useEffect(() => {
    const id = eventId || event?.id;
    if (!id) return;
    (async () => {
      const eventPolls = await getAllPollsForEvent(id);
      setPolls(eventPolls);
    })();
  }, [eventId, event?.id]);

  // Load poll templates
  useEffect(() => {
    if (!user?.uid) return;
    (async () => {
      const templates = await getPollTemplates(user.uid);
      setPollTemplates(templates);
    })();
  }, [user?.uid]);

  // Poll handlers
  const handleCreatePoll = async (values: PollBuilderValues, status: 'draft' | 'active') => {
    const id = eventId || event?.id;
    if (!id || !user?.uid) return;
    try {
      await createPoll(id, user.uid, values.title, values.questions, status, values.settings);
      const updatedPolls = await getAllPollsForEvent(id);
      setPolls(updatedPolls);
      setShowPollBuilder(false);
      toast.success(status === 'active' ? 'Poll sent to attendees!' : 'Poll saved as draft');
    } catch (err) {
      throw err; // Re-throw so PollBuilder shows the error
    }
  };

  const handleResendPoll = async (pollId: string) => {
    try {
      await resendPoll(pollId);
      setPolls(prev => prev.map(p =>
        p.id === pollId ? { ...p, lastResendAt: new Date().toISOString() } : p
      ));
      toast.success('Poll resent — attendees will see it again');
    } catch {
      toast.error('Failed to resend poll');
    }
  };

  const handleClosePoll = async (pollId: string) => {
    try {
      await closePoll(pollId);
      setPolls(prev => prev.map(p => p.id === pollId ? { ...p, status: 'closed' as const, closedAt: new Date().toISOString() } : p));
      toast.success('Poll closed');
    } catch {
      toast.error('Failed to close poll');
    }
  };

  const handleDeletePoll = async (pollId: string) => {
    if (!confirm('Delete this poll and all responses?')) return;
    try {
      await deletePoll(pollId);
      setPolls(prev => prev.filter(p => p.id !== pollId));
      toast.success('Poll deleted');
    } catch {
      toast.error('Failed to delete poll');
    }
  };

  const handleToggleResults = async (pollId: string, current: boolean) => {
    try {
      await toggleResultVisibility(pollId, !current);
      setPolls(prev => prev.map(p => p.id === pollId ? { ...p, showResultsToAttendees: !current } : p));
      toast.success(!current ? 'Results visible to attendees' : 'Results hidden from attendees');
    } catch {
      toast.error('Failed to update result visibility');
    }
  };

  const handleSavePollAsTemplate = async (poll: Poll) => {
    if (!user?.uid) return;
    try {
      await savePollTemplate(user.uid, {
        title: poll.title,
        questions: poll.questions,
        settings: {
          mandatory: poll.mandatory,
          showToNewUsers: poll.showToNewUsers,
          editable: poll.editable,
          showResultsToAttendees: poll.showResultsToAttendees,
        },
        isDefault: false,
        eventTemplateIds: [],
        arrival: 'draft',
        arrivalOffsetMinutes: 0,
      });
      const templates = await getPollTemplates(user.uid);
      setPollTemplates(templates);
      toast.success('Poll saved as template');
    } catch {
      toast.error('Failed to save template');
    }
  };

  const handleDeletePollTemplate = async (templateId: string) => {
    try {
      await deletePollTemplate(templateId);
      setPollTemplates(prev => prev.filter(t => t.id !== templateId));
      toast.success('Template deleted');
    } catch {
      toast.error('Failed to delete template');
    }
  };

  const handleUseTemplate = (template: PollTemplate) => {
    setEditingFromTemplate({ title: template.title, questions: template.questions, settings: template.settings });
    setShowPollTemplates(false);
    setShowPollBuilder(true);
  };

  const reloadPolls = async () => {
    const id = eventId || event?.id;
    if (!id) return;
    try {
      setPolls(await getAllPollsForEvent(id));
    } catch (err) {
      console.error('[MessagesPage] Could not reload polls:', err);
    }
  };

  const handleSendPollNow = async (pollId: string) => {
    try {
      const update = buildSendNowUpdate();
      await updatePoll(pollId, update);
      setPolls(prev => prev.map(p => p.id === pollId ? { ...p, ...update } : p));
      toast.success('Poll sent to attendees!');
      await reloadPolls();
    } catch (err) {
      console.error('[MessagesPage] Could not send poll:', err);
      toast.error('Failed to send poll');
    }
  };

  const handleCancelAutoSend = async (pollId: string) => {
    try {
      const update = buildCancelAutoSendUpdate();
      await updatePoll(pollId, update);
      setPolls(prev => prev.map(p => p.id === pollId ? { ...p, ...update } : p));
      toast.success('Auto-send turned off');
      await reloadPolls();
    } catch (err) {
      console.error('[MessagesPage] Could not turn off auto-send:', err);
      toast.error('Failed to turn off auto-send');
    }
  };

  // Format timestamp
  const formatTime = (date: Date) => {
    return new Date(date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Messages</h2>
        <p className="text-gray-600 dark:text-gray-400">
          Communicate with your audience
        </p>
      </div>

      {/* Polls Section */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-2">
            <BarChart3 className="w-4 h-4" />
            Polls & Surveys
          </h3>
          {!isLocked && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowPollTemplates(true)}
                className="btn btn-secondary btn-sm flex items-center gap-1 text-xs"
              >
                <Bookmark className="w-3 h-3" /> Library ({pollTemplates.length})
              </button>
              <button
                onClick={() => { setEditingFromTemplate(null); setShowPollBuilder(true); }}
                className="btn btn-primary btn-sm flex items-center gap-1 text-xs"
              >
                <Plus className="w-3 h-3" /> Create Poll
              </button>
            </div>
          )}
        </div>

        {/* Poll Templates Modal */}
        {showPollTemplates && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => setShowPollTemplates(false)}>
            <div className="absolute inset-0 bg-black/50" />
            <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full max-h-[70vh] flex flex-col" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
                <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                  <Bookmark className="w-5 h-5 text-primary" />
                  My Polls
                </h3>
                <button onClick={() => setShowPollTemplates(false)} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-2">
                {pollTemplates.length === 0 && (
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    No saved polls yet. Save a poll below with the bookmark button, or create one in your library.
                  </p>
                )}
                {pollTemplates.map(template => (
                  <div key={template.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-sm text-gray-900 dark:text-white truncate">{template.title}</p>
                      <p className="text-xs text-gray-500">{template.questions.length} question{template.questions.length !== 1 ? 's' : ''}</p>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button
                        onClick={() => handleUseTemplate(template)}
                        className="btn btn-primary btn-sm text-xs flex items-center gap-1"
                      >
                        <Copy className="w-3 h-3" /> Use
                      </button>
                      <button
                        onClick={() => handleDeletePollTemplate(template.id)}
                        className="p-1.5 rounded hover:bg-red-100 dark:hover:bg-red-900/30 text-gray-400 hover:text-red-500 transition-colors"
                        title="Delete template"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              <div className="p-4 border-t border-gray-200 dark:border-gray-700">
                <Link to="/settings?tab=polls" className="text-sm font-medium text-primary hover:underline">
                  Manage library
                </Link>
              </div>
            </div>
          </div>
        )}

        {/* Poll Builder Modal */}
        {showPollBuilder && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => { setShowPollBuilder(false); setEditingFromTemplate(null); }}>
            <div className="absolute inset-0 bg-black/50" />
            <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-lg w-full max-h-[85vh] overflow-y-auto p-6" onClick={e => e.stopPropagation()}>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                {editingFromTemplate ? 'Create Poll from Template' : 'Create Poll'}
              </h3>
              <PollBuilder
                onSubmit={handleCreatePoll}
                onCancel={() => { setShowPollBuilder(false); setEditingFromTemplate(null); }}
                initialTitle={editingFromTemplate?.title}
                initialQuestions={editingFromTemplate?.questions}
                initialSettings={editingFromTemplate?.settings}
              />
            </div>
          </div>
        )}

        {/* Results Viewer Modal */}
        {viewingResultsFor && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => setViewingResultsFor(null)}>
            <div className="absolute inset-0 bg-black/50" />
            <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-lg w-full max-h-[80vh] overflow-y-auto p-6" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {polls.find(p => p.id === viewingResultsFor)?.title} — Results
                </h3>
                <button onClick={() => setViewingResultsFor(null)} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <PollResults pollId={viewingResultsFor} />
            </div>
          </div>
        )}

        {/* Poll List */}
        {polls.length > 0 && (
          <div className="space-y-2">
            {polls.map(poll => (
              <div key={poll.id} className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-medium text-gray-900 dark:text-white text-sm truncate">{poll.title}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${
                        poll.status === 'active' ? 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-400' :
                        poll.status === 'draft' ? 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400' :
                        'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-400'
                      }`}>
                        {poll.status}
                      </span>
                      <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${
                        poll.mandatory
                          ? 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-400'
                          : 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                      }`}>
                        {poll.mandatory ? 'mandatory' : 'optional'}
                      </span>
                      {poll.showToNewUsers && (
                        <span className="text-xs px-1.5 py-0.5 rounded-full font-medium bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-400">
                          sticky
                        </span>
                      )}
                      {poll.editable && (
                        <span className="text-xs px-1.5 py-0.5 rounded-full font-medium bg-teal-100 dark:bg-teal-900/40 text-teal-700 dark:text-teal-400">
                          editable
                        </span>
                      )}
                      {poll.status === 'draft' && typeof poll.autoSendOffsetMinutes === 'number' && (
                        <span className="text-xs px-1.5 py-0.5 rounded-full font-medium bg-sky-100 dark:bg-sky-900/40 text-sky-700 dark:text-sky-400">
                          auto-send
                        </span>
                      )}
                      <span className="text-xs text-gray-500">{poll.responseCount} responses</span>
                      <span className="text-xs text-gray-500">{poll.questions.length} question{poll.questions.length !== 1 ? 's' : ''}</span>
                    </div>
                    {(() => {
                      const autoSend = describeAutoSend(poll);
                      if (!autoSend) return null;
                      return (
                        <p className={`text-xs mt-1 ${autoSend.tone === 'warning' ? 'text-amber-600 dark:text-amber-400' : 'text-gray-500 dark:text-gray-400'}`}>
                          {autoSend.text}
                        </p>
                      );
                    })()}
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      onClick={() => setViewingResultsFor(poll.id)}
                      className="p-1.5 rounded hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-500 transition-colors"
                      title="View Results"
                    >
                      <BarChart3 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleToggleResults(poll.id, poll.showResultsToAttendees)}
                      className={`p-1.5 rounded hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors ${
                        poll.showResultsToAttendees ? 'text-green-500' : 'text-gray-400'
                      }`}
                      title={poll.showResultsToAttendees ? 'Results visible to attendees (click to hide)' : 'Results hidden from attendees (click to show)'}
                    >
                      {poll.showResultsToAttendees ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
                    </button>
                    {poll.status === 'draft' && (
                      <button
                        onClick={() => handleSendPollNow(poll.id)}
                        className="p-1.5 rounded hover:bg-green-100 dark:hover:bg-green-900/30 text-green-500 transition-colors"
                        title="Activate Poll"
                      >
                        <Send className="w-4 h-4" />
                      </button>
                    )}
                    {poll.status === 'draft' && typeof poll.autoSendOffsetMinutes === 'number' && (
                      <button
                        onClick={() => handleCancelAutoSend(poll.id)}
                        className="p-1.5 rounded hover:bg-sky-100 dark:hover:bg-sky-900/30 text-sky-500 transition-colors"
                        title="Don't send automatically"
                        aria-label="Don't send automatically"
                      >
                        <Clock className="w-4 h-4" />
                      </button>
                    )}
                    {poll.status === 'active' && (
                      <button
                        onClick={() => handleClosePoll(poll.id)}
                        className="p-1.5 rounded hover:bg-red-100 dark:hover:bg-red-900/30 text-red-500 transition-colors"
                        title="Close Poll"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                    {poll.editable && (poll.status === 'active' || poll.showToNewUsers) && (
                      <button
                        onClick={() => handleResendPoll(poll.id)}
                        className="p-1.5 rounded hover:bg-purple-100 dark:hover:bg-purple-900/30 text-purple-500 transition-colors"
                        title="Resend Poll (re-show overlay + push notification)"
                      >
                        <RefreshCw className="w-4 h-4" />
                      </button>
                    )}
                    <button
                      onClick={() => handleSavePollAsTemplate(poll)}
                      className="p-1.5 rounded hover:bg-blue-100 dark:hover:bg-blue-900/30 text-gray-400 hover:text-blue-500 transition-colors"
                      title="Save as Template"
                    >
                      <Bookmark className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDeletePoll(poll.id)}
                      className="p-1.5 rounded hover:bg-red-100 dark:hover:bg-red-900/30 text-gray-400 hover:text-red-500 transition-colors"
                      title="Delete Poll"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <hr className="border-gray-200 dark:border-gray-700" />

      {/* Sent Confirmation Banner */}
      {showSentConfirmation && (
        <div className="bg-success/10 border border-success/20 rounded-lg p-4 flex items-center gap-3" role="alert">
          <CheckCircle className="w-5 h-5 text-success flex-shrink-0" />
          <div>
            <p className="font-medium text-success">Message Sent Successfully!</p>
            <p className="text-sm text-success/80">Your message has been delivered to all attendees.</p>
          </div>
        </div>
      )}

      {/* Messages List */}
      <div className="h-96 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg p-4">
        {messages.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-center">
            <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-4">
              <MessageSquare className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              No Messages Yet
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              Send a message to your attendees below
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {messages.map((msg) => {
              // Get read stats for this message (pre-fetched)
              const readStats = messageReadStats[msg.id] || { readCount: 0 };
              const readPercentage = estimatedAttendees > 0
                ? Math.round((readStats.readCount / estimatedAttendees) * 100)
                : 0;

              return (
                <div
                  key={msg.id}
                  className={`flex ${msg.sentBy === 'dj' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[70%] p-3 rounded-lg ${
                      msg.sentBy === 'dj'
                        ? 'bg-primary text-white'
                        : 'bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <p className="text-sm font-medium">
                        {msg.sentBy === 'dj' ? 'You' : msg.senderName}
                      </p>
                      {msg.sentBy === 'dj' && (
                        <span className={`text-xs px-1.5 py-0.5 rounded ${
                          msg.sentBy === 'dj' ? 'bg-white/20' : 'bg-gray-200 dark:bg-gray-700'
                        }`}>
                          {msg.audience === 'all' && <Users className="w-3 h-3 inline mr-1" />}
                          {msg.audience === 'top-voters' && <ThumbsUp className="w-3 h-3 inline mr-1" />}
                          {msg.audience === 'no-requests' && <UserX className="w-3 h-3 inline mr-1" />}
                          {msg.audienceLabel || 'All'}
                        </span>
                      )}
                    </div>
                    <p className="text-sm">{msg.content}</p>
                    <div className={`flex items-center justify-between mt-2 ${
                      msg.sentBy === 'dj' ? 'text-white/70' : 'text-gray-500'
                    }`}>
                      <p className="text-xs">
                        {formatTime(msg.sentAt)}
                      </p>
                      {/* Read Receipt Stats */}
                      {msg.sentBy === 'dj' && (
                        <div className="flex items-center gap-2 text-xs" title={`${readStats.readCount} attendee${readStats.readCount !== 1 ? 's' : ''} read this message`}>
                          <Eye className="w-3 h-3" />
                          <span>{readStats.readCount}</span>
                          {estimatedAttendees > 0 && (
                            <span className="bg-white/20 px-1.5 py-0.5 rounded">
                              {readPercentage}%
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Message Composition Area */}
      <div className="space-y-4">
        {/* Audience Selector */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Send to
          </label>
          <div className="flex flex-wrap gap-2">
            {AUDIENCE_OPTIONS.map((option) => {
              const Icon = option.id === 'all' ? Users : option.id === 'top-voters' ? ThumbsUp : UserX;
              return (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => setAudience(option.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors ${
                    audience === option.id
                      ? 'bg-primary text-white border-primary'
                      : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:border-primary'
                  }`}
                  aria-pressed={audience === option.id}
                  title={option.description}
                >
                  <Icon className="w-4 h-4" />
                  {option.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Template Selector */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              <FileText className="w-4 h-4 inline mr-1" />
              Quick Templates
            </label>
            <button
              type="button"
              onClick={() => setShowManageTemplates(true)}
              className="text-xs text-primary hover:text-primary/80 flex items-center gap-1"
            >
              <Settings className="w-3 h-3" />
              Manage Templates
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {cannedTemplates.map((template) => (
              <button
                key={template.id}
                type="button"
                onClick={() => setMessage(template.text)}
                className="px-3 py-1.5 text-sm rounded-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:border-primary hover:text-primary transition-colors"
              >
                {template.label}
              </button>
            ))}
          </div>
        </div>

        {/* Message Input */}
        {!isLocked && <form onSubmit={handleSend} className="space-y-3">
          <div>
            <label htmlFor="message-input" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Message
            </label>
            <textarea
              id="message-input"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Type your message to attendees..."
              className="input min-h-[100px] resize-none"
              rows={3}
            />
          </div>
          <div className="flex gap-2">
            <button
              type="submit"
              className="btn btn-primary flex-1"
              disabled={!message.trim()}
            >
              <Send className="w-5 h-5 mr-2" />
              Send Message
            </button>
            {message.trim() && (
              <>
                <button
                  type="button"
                  onClick={handleSaveAsTemplate}
                  className="btn btn-secondary"
                  title="Save as template"
                >
                  <Save className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={handleSaveAndSend}
                  className="btn btn-secondary text-sm"
                  title="Save as template and send"
                >
                  <Save className="w-4 h-4 mr-1" />
                  <Send className="w-4 h-4" />
                </button>
              </>
            )}
          </div>
        </form>}
      </div>

      {/* Manage Templates Modal */}
      {showManageTemplates && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowManageTemplates(false)} />
          <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-lg w-full mx-4 p-6 max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <FileText className="w-5 h-5 text-primary" />
                Manage Templates
              </h3>
              <button
                onClick={() => setShowManageTemplates(false)}
                className="p-1 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Existing templates */}
            <div className="space-y-2 mb-6">
              {cannedTemplates.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">No templates yet. Add one below.</p>
              ) : (
                cannedTemplates.map((template) => (
                  <div
                    key={template.id}
                    className="flex items-start justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg"
                  >
                    <div className="flex-1 min-w-0 mr-3">
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{template.label}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-2">{template.text}</p>
                    </div>
                    <button
                      onClick={() => handleDeleteTemplate(template.id)}
                      className="p-1.5 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 text-gray-400 hover:text-red-500 transition-colors flex-shrink-0"
                      title="Delete template"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))
              )}
            </div>

            {/* Add new template */}
            <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Add Custom Template</h4>
              <div className="space-y-3">
                <input
                  type="text"
                  value={newTemplateLabel}
                  onChange={(e) => setNewTemplateLabel(e.target.value)}
                  placeholder="Label (e.g., 'Dance floor open!')"
                  className="input w-full text-sm"
                  maxLength={60}
                />
                <textarea
                  value={newTemplateText}
                  onChange={(e) => setNewTemplateText(e.target.value)}
                  placeholder="Message text..."
                  className="input w-full text-sm resize-none"
                  rows={2}
                  maxLength={500}
                />
                <button
                  type="button"
                  onClick={handleAddCustomTemplate}
                  disabled={!newTemplateLabel.trim() || !newTemplateText.trim()}
                  className="btn btn-primary w-full text-sm"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Template
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
